import { t } from "i18next";
import React from "react";
import { useGetPartList } from "../../hooks/index.js";
import * as API from "../../lib/api.js";
import Wait from "../Wait/Wait.jsx";
import BackNext from "./BackNext.jsx";
import MobileMenu from "./MobileMenu.jsx";
import WizardCheck from "./WizardCheck.js";
import DesktopMenu from "./WizardDesktopMenu.jsx";

const { MultipleCheck } = WizardCheck;

export default function WizardFormEditECommerce(props) {
    let { startPart, currentPart, setCurrentPart, variables, setVariables, structure } = props;

    const PartList = useGetPartList({ type: "ecommerce" });

    const refWizard = React.useRef(null);

    let [abort, setAbort] = React.useState(null);
    const [wait, setWait] = React.useState(false);

    if (typeof currentPart === "string") {
        currentPart = PartList.filter(({ key }) => key === currentPart)[0];
        currentPart = currentPart || props.startStep || PartList[0];
    } else if (!currentPart) {
        currentPart = currentPart || props.startStep || PartList[0];
    }

    function save(data) {
        setWait(true);
        abort && abort.abort();
        setAbort((abort = new AbortController()));
        return new Promise(resolve =>
            API.WizardSave(data, { signal: abort.signal })
                .then(value => {
                    setAbort(null);
                    resolve(value);
                    setWait(false);
                })
                .catch(() => {})
        );
    }

    function isCurrentPart(targetPart) {
        return targetPart.key === currentPart.key;
    }

    function isPrevPart(targetPart) {
        for (const part of PartList) {
            if (part.key === currentPart.key) {
                return false;
            }

            if (part.key === targetPart.key) {
                return true;
            }
        }
    }

    function partIndex(targetPart) {
        const index = PartList.findIndex(part => part.key === targetPart.key);
        return index;
    }

    function prev() {
        const indexPrev = Math.max(0, partIndex(currentPart) - 1);
        const newPart = PartList[indexPrev];
        setCurrentPart(newPart);

        // TODO - Temporary wizard (mongo) not save variables
        // save({ partKey: newPart.key, variables });
        save({ partKey: newPart.key });
    }

    function next(event, updVariables) {
        const indexNext = Math.min(PartList.length - 1, partIndex(currentPart) + 1);
        const newPart = PartList[indexNext];

        const newVariables = { ...variables, ...updVariables };

        setVariables(newVariables);
        setCurrentPart(newPart);

        // TODO - Temporary wizard (mongo) not save variables
        // save({ partKey: newPart.key, variables: newVariables });
        save({ partKey: newPart.key });
    }

    function set(updVariables, item) {
        if (item?.id) {
            const newV2B = [
                ...variables.variables2block.map(_item => {
                    if (_item.id !== item.id) {
                        return _item;
                    }

                    return {
                        ..._item,
                        variables: {
                            ...item.variables,
                            ...updVariables,
                        },
                    };
                }),
            ];

            setVariables({ ...variables, variables2block: newV2B });

            return;
        }

        setVariables({ ...variables, ...updVariables });
    }

    function setStepByIndex(index) {
        const newPart = PartList[index];
        setCurrentPart(newPart);

        // TODO - Temporary wizard (mongo) not save variables
        // save({ partKey: newPart.key, variables });
        save({ partKey: newPart.key });
    }

    function onFormSubmit(event) {
        event.preventDefault();
    }

    function onFormKeyDown(event) {
        if ((event.ctrlKey || event.metaKey) && event.keyCode === 13) {
            event.preventDefault();
            next();
        }
    }

    function CalcComplete() {
        const completeSteps = {};

        for (const part of PartList) {
            if (!part.key || !part.component) {
                continue;
            }

            const pk = PartList.indexOf(part);

            completeSteps[pk] = {
                complete: true,
                filled: false,
                fullfilled: false,
                vars: {},
            };

            if (part.requires) {
                // const vars = {};
                const { vars } = completeSteps[pk];

                for (const varName of part.requires) {
                    vars[varName] = variables[varName];

                    completeSteps[pk].filled =
                        completeSteps[pk].filled || MultipleCheck({ [varName]: variables[varName] }, false);
                }

                completeSteps[pk].fullfilled = MultipleCheck(vars, false);
            }
        }

        let allComplete =
            Object.values(completeSteps).filter(({ fullfilled }) => fullfilled).length ===
            Object.keys(completeSteps).length;

        return { completeSteps, allComplete };
    }

    const { completeSteps, allComplete } = CalcComplete();

    const partProps = {
        prev,
        next,
        set,
        isCustom: variables.ProductType === "Custom",
        cancel: props.onCancel,
        save: props.onSave,
        variables,
        allComplete,
    };

    const CLWizard = ["wizard", "wizard-edit"];
    props.hide && CLWizard.push("hide");

    return (
        <>
            <div className="wizard-top-menu">
                <div className="preview-menu-logo">
                    pgbldr.com <span className="color-placeholder fw-normal">/{t("Editing eCommerce settings")}</span>
                </div>

                <BackNext cancel={props.onCancel} save={props.onSave} allowNext={true} />

                <MobileMenu
                    currentPart={currentPart}
                    partIndex={partIndex}
                    partList={PartList}
                    stepList={PartList}
                    completeSteps={completeSteps}
                    setStepByIndex={setStepByIndex}
                />
            </div>

            <form
                ref={refWizard}
                className={CLWizard.join(" ")}
                action="."
                onSubmit={onFormSubmit}
                onKeyDown={event => {
                    if (completeSteps[partIndex(currentPart)]) {
                        onFormKeyDown(event);
                    }
                }}
                autoComplete="false"
            >
                <div className="wizard-inner">
                    <DesktopMenu
                        currentPart={currentPart}
                        partIndex={partIndex}
                        partList={PartList}
                        completeSteps={completeSteps}
                        setStepByIndex={setStepByIndex}
                    />

                    {PartList.map(part => {
                        const Component = part.component;
                        if (!Component) {
                            return null;
                        }

                        const className = ["wizard-part wizard-part-ecommerce"];
                        isCurrentPart(part) && className.push("current");

                        isPrevPart(part) && className.push("prev");

                        const spreadPartProps = { ...partProps };
                        part.isLast && (spreadPartProps.isLast = true);
                        isCurrentPart(part) && (spreadPartProps.isCurrentPart = true);

                        spreadPartProps.prev = (() => {
                            if (!startPart) {
                                return spreadPartProps.prev;
                            } else if (PartList.indexOf(part) > PartList.indexOf(startPart)) {
                                return spreadPartProps.prev;
                            } else {
                                return null;
                            }
                        })();

                        return (
                            <div key={part.key} className={className.join(" ")}>
                                <Component {...spreadPartProps} variables2block={part.variables2block} />
                            </div>
                        );
                    })}
                </div>
            </form>

            <Wait show={wait} />
        </>
    );
}
